import {
  UpdateEffortStatusContactDialog,
  CreateEffortStatusContactDialog,
  DeleteEffortStatusContactDialog,
} from './components';
import { useSnackbar } from 'notistack';
import { useAppSelector } from 'store/hooks';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { EFFORT_STATUS_TYPE_ENUM, STORAGE_KEY_ENUM } from 'enums';
import { EffortContactItem, EffortStatusContactItem } from 'library/types';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { DataGridBox, CustomToolbar, ZebraDataGrid, PermissionHandler, CustomNoRowsOverlay } from 'components';

const EffortStatusContacts = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [effortContacts, setEffortContacts] = useState<EffortContactItem[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [openDeleteEffortStatusContact, setOpenDeleteEffortStatusContact] = useState(false);
  const [openCreateEffortStatusContact, setOpenCreateEffortStatusContact] = useState(false);
  const [openUpdateEffortStatusContact, setOpenUpdateEffortStatusContact] = useState(false);
  const [effortStatusContacts, setEffortStatusContacts] = useState<EffortStatusContactItem[]>([]);

  // translate functions
  const getEffortStatusTypeTranslated = useCallback(
    (effortStatusType: number) =>
      ({
        [EFFORT_STATUS_TYPE_ENUM.IN_SALES]: intl.formatMessage({
          id: 'enum.effortStatusType.inSales',
          defaultMessage: 'Satış Ekibinde',
        }),
        [EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL]: intl.formatMessage({
          id: 'enum.effortStatusType.pendingCustomerApproval',
          defaultMessage: 'Müşteri Onayında',
        }),
        [EFFORT_STATUS_TYPE_ENUM.APPROVED]: intl.formatMessage({
          id: 'enum.effortStatusType.approved',
          defaultMessage: 'Efor Onaylandı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.REJECTED]: intl.formatMessage({
          id: 'enum.effortStatusType.rejected',
          defaultMessage: 'Efor Reddedildi',
        }),
        [EFFORT_STATUS_TYPE_ENUM.WORK_STARTED]: intl.formatMessage({
          id: 'enum.effortStatusType.workStarted',
          defaultMessage: 'Çalışma Başlatıldı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER]: intl.formatMessage({
          id: 'enum.effortStatusType.testingByCustomer',
          defaultMessage: 'Müşteri Testinde',
        }),
        [EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED]: intl.formatMessage({
          id: 'enum.effortStatusType.workCompleted',
          defaultMessage: 'Çalışma Tamamlandı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.INVOICED]: intl.formatMessage({
          id: 'enum.effortStatusType.invoiced',
          defaultMessage: 'Faturası Kesildi',
        }),
      })[effortStatusType],
    [intl]
  );

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: intl.formatMessage({
          id: 'effortTracing.status',
          defaultMessage: 'Statü',
        }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 250,
        valueGetter: (value) => getEffortStatusTypeTranslated(value),
      },
      {
        field: 'toContacts',
        headerName: 'TO',
        flex: 1,
        valueGetter: (value: number[]) =>
          value.map((v) => effortContacts.find((effortContact) => effortContact.id === v)?.title).join(', '),
      },
      {
        field: 'ccContacts',
        headerName: 'CC',
        flex: 1,
        valueGetter: (value: number[]) =>
          value.map((v) => effortContacts.find((effortContact) => effortContact.id === v)?.title).join(', '),
      },
    ],
    [intl, effortContacts, getEffortStatusTypeTranslated]
  );

  // sync helper functions
  const handleCloseDeleteEffortStatusContact = () => setOpenDeleteEffortStatusContact(false);
  const handleClickOpenDeleteEffortStatusContact = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteEffortStatusContact(true);
  };

  const handleCloseCreateEffortStatusContact = () => setOpenCreateEffortStatusContact(false);
  const handleClickOpenCreateEffortStatusContact = () => setOpenCreateEffortStatusContact(true);

  const handleCloseUpdateEffortStatusContact = () => setOpenUpdateEffortStatusContact(false);
  const handleClickOpenUpdateEffortStatusContact = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateEffortStatusContact(true);
  };

  // async helper functions
  const getEffortContacts = async () => {
    try {
      const response = await apiService.getEffortContacts();

      if (response.isSuccess) {
        setEffortContacts(response.data);
      }
    } catch {}
  };

  const getEffortStatusContacts = async () => {
    try {
      setLoading(true);

      const response = await apiService.getEffortStatusContacts();

      if (response.isSuccess) {
        const duplicateArr = [...response.data];
        const sortedData = duplicateArr.sort((a, b) => a.status - b.status);

        setEffortStatusContacts(sortedData);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-effort-status-contact')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getEffortContacts();
  }, []);

  useEffect(() => {
    if (effortContacts.length) {
      getEffortStatusContacts();
    }
  }, [effortContacts]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-effort-status-contact']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenCreateEffortStatusContact}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage
                id="effortStatusContacts.createEffortStatusContact"
                defaultMessage="Efor Statü Muhatabı Oluştur"
              />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-effort-status-contact']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenUpdateEffortStatusContact}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage
                id="effortStatusContacts.updateEffortStatusContact"
                defaultMessage="Efor Statü Muhatabı Düzenle"
              />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-effort-status-contact']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenDeleteEffortStatusContact}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage
                id="effortStatusContacts.deleteEffortStatusContact"
                defaultMessage="Efor Statü Muhatabı Sil"
              />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateEffortStatusContactDialog
        open={openCreateEffortStatusContact}
        handleClose={handleCloseCreateEffortStatusContact}
        refresh={getEffortStatusContacts}
        effortContacts={effortContacts}
        effortStatusContacts={effortStatusContacts}
        translateFunction={getEffortStatusTypeTranslated}
      />
      <UpdateEffortStatusContactDialog
        open={openUpdateEffortStatusContact}
        effortStatusContactId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateEffortStatusContact}
        refresh={getEffortStatusContacts}
        effortContacts={effortContacts}
        translateFunction={getEffortStatusTypeTranslated}
      />
      <DeleteEffortStatusContactDialog
        open={openDeleteEffortStatusContact}
        effortStatusContactId={rowSelectionModel[0]}
        handleClose={handleCloseDeleteEffortStatusContact}
        refresh={getEffortStatusContacts}
        setRowSelectionModel={setRowSelectionModel}
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={effortStatusContacts}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default EffortStatusContacts;
